/**
 * TikTok Pixel loader — consent-gated.
 *
 * The pixel base code is NOT injected into `index.html` (the previous
 * approach would fire before the user has a chance to consent, breaking
 * GDPR/UK-GDPR/CCPA-lite compliance and TikTok's own consent requirements
 * for EU traffic). Instead we lazily inject the script only after the user
 * has explicitly granted analytics/marketing consent, and we replay a
 * queued `PageView` for the current route.
 *
 * Events wired in Phase 1:
 *   • PageView          — every route change once consent is granted
 *   • InitiateCheckout  — fired from SubscriptionPage the moment the user
 *                          taps "Start 7-day free trial" (before the Paddle
 *                          overlay opens). Same event GA4 gets `begin_checkout`.
 *   • CompletePayment   — fired ONLY inside Paddle's `checkout.completed`
 *                          eventCallback, i.e. after the payment has been
 *                          confirmed by Paddle. Never fired on overlay open,
 *                          close, or return-to-app without payment.
 */

import {
    CONSENT_GRANTED,
    getConsent,
    onConsentChange,
} from "./consent";

const PIXEL_ID = "D9PMPDJC77U4UUP3J0G0"; // PawLink main pixel

let _loaded = false;         // has the TikTok script been injected?
let _hasPending = false;     // was PageView queued while unloaded?
let _boundConsent = false;   // subscription installed?

function _inject() {
    if (_loaded) return;
    // If the early bootstrap in index.html already loaded the pixel for our
    // ID (user had prior consent), don't re-inject the base code — just mark
    // ourselves as loaded so subsequent track() calls flow through window.ttq.
    if (typeof window !== "undefined" && window.ttq && window.ttq._i && window.ttq._i[PIXEL_ID]) {
        _loaded = true;
        return;
    }
    _loaded = true;
    // Standard TikTok Pixel base code, adapted to be idempotent and to skip
    // if analytics is disabled (localhost, preview screenshot bots, etc.).
    // Reference: https://ads.tiktok.com/help/article/get-started-pixel
    // eslint-disable-next-line
    !function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        var ttq = w[t] = w[t] || [];
        ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
        ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function (t) {
            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
            return e;
        };
        ttq.load = function (e, n) {
            var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
            var o = n && n.partner;
            ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r;
            ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
            ttq._o = ttq._o || {}; ttq._o[e] = n || {};
            n = document.createElement("script");
            n.type = "text/javascript"; n.async = !0;
            n.src = r + "?sdkid=" + e + "&lib=" + t;
            e = document.getElementsByTagName("script")[0];
            e.parentNode.insertBefore(n, e);
        };
        ttq.load(PIXEL_ID);
    }(window, document, "ttq");
}

function _canFire() {
    return typeof window !== "undefined" && getConsent() === CONSENT_GRANTED;
}

/**
 * Called on app start AND on every consent change. Only injects and fires
 * PageView if consent is granted. Withdrawal (denied) does NOT unload the
 * script (TikTok has no such API) but blocks any further track() calls.
 */
export function initTikTokPixel() {
    if (!_boundConsent) {
        _boundConsent = true;
        onConsentChange((val) => {
            if (val === CONSENT_GRANTED) {
                _inject();
                if (_hasPending) {
                    _hasPending = false;
                    trackPageView();
                }
            }
        });
    }
    if (_canFire()) {
        _inject();
        trackPageView();
    }
}

export function trackPageView() {
    if (!_canFire()) { _hasPending = true; return; }
    _inject();
    try { window.ttq && window.ttq.page(); } catch { /* noop */ }
}

export function trackInitiateCheckout({ value, currency = "CHF", contentId, contentName } = {}) {
    if (!_canFire()) return;
    _inject();
    try {
        window.ttq && window.ttq.track("InitiateCheckout", {
            value,
            currency,
            contents: contentId ? [{
                content_id: contentId,
                content_type: "product",
                content_name: contentName,
                quantity: 1,
                price: value,
            }] : undefined,
        });
    } catch { /* noop */ }
}

export function trackCompletePayment({ value, currency = "CHF", orderId, contentId, contentName } = {}) {
    if (!_canFire()) return;
    _inject();
    try {
        window.ttq && window.ttq.track("CompletePayment", {
            value,
            currency,
            order_id: orderId,
            contents: contentId ? [{
                content_id: contentId,
                content_type: "product",
                content_name: contentName,
                quantity: 1,
                price: value,
            }] : undefined,
        });
    } catch { /* noop */ }
}
